import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Grid,
  GridItem,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  VStack,
  Text,
} from "@chakra-ui/react";
import * as Yup from "yup";
import axios from "axios";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { validateFormAsync } from "../../../utils/validationRules";

const SERVER_URL = import.meta.env.VITE_API_URL || "http://localhost:4500";

// Validation schema for global settings
const globalSettingsSchema = Yup.object({
  customCommission: Yup.number()
    .nullable()
    .typeError("Custom Commission must be a number.")
    .test(
      "not-required",
      "At least one field must have a value.",
      function (value) {
        const { tollTaxPerKm } = this.parent;
        return value !== null || tollTaxPerKm !== null;
      }
    )
    .min(0, "Custom Commission cannot be negative.")
    .max(100, "Custom Commission cannot exceed 100%.")
    .test(
      "decimal-places",
      "Custom Commission can have maximum 2 decimal places.",
      (val) => !val || val.toString().split(".")[1]?.length <= 2
    ),

  tollTaxPerKm: Yup.number()
    .nullable()
    .typeError("Toll Tax Per KM must be a number.")
    .test(
      "not-required",
      "At least one field must have a value.",
      function (value) {
        const { customCommission } = this.parent;
        return value !== null || customCommission !== null;
      }
    )
    .min(0, "Toll Tax Per KM cannot be negative.")
    .max(10000, "Toll Tax Per KM cannot exceed ₹10,000.")
    .test(
      "decimal-places",
      "Toll Tax Per KM can have maximum 2 decimal places.",
      (val) => !val || val.toString().split(".")[1]?.length <= 2
    ),
});

const GlobalFields = () => {
  const toast = useToast();
  const [globalSettings, setGlobalSettings] = useState({
    customCommission: "",
    tollTaxPerKm: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load settings from backend on mount
  useEffect(() => {
    const fetchGlobalSettings = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("prima_session");
        const res = await axios.get(`${SERVER_URL}/api/global-fields`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });

        const commission = res?.data?.data?.globalCommission;
        const tollTax = res?.data?.data?.globalTollTax;

        setGlobalSettings({
          customCommission: commission !== null && commission !== undefined ? commission : "",
          tollTaxPerKm: tollTax !== null && tollTax !== undefined ? tollTax : "",
        });
      } catch (error) {
        console.error("Error fetching global settings:", error);
        toast({
          title: "Error",
          description: "Failed to load global settings",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchGlobalSettings();
  }, [toast]);

  // Real-time validation on input change
  const validateField = async (name, value) => {
    try {
      const fieldSchema = Yup.object({
        [name]: globalSettingsSchema.fields[name],
      });

      const testData = {
        [name]: value === "" ? null : value,
        [name === "customCommission" ? "tollTaxPerKm" : "customCommission"]:
          name === "customCommission"
            ? globalSettings.tollTaxPerKm === ""
              ? null
              : globalSettings.tollTaxPerKm
            : globalSettings.customCommission === ""
            ? null
            : globalSettings.customCommission,
      };

      await fieldSchema.validate(testData);
      
      // Clear error if validation passes
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    } catch (error) {
      if (error.message) {
        setErrors((prev) => ({
          ...prev,
          [name]: error.message,
        }));
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Allow empty string or valid numeric input
    if (value === "" || !isNaN(value)) {
      setGlobalSettings((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Mark field as touched
      setTouched((prev) => ({
        ...prev,
        [name]: true,
      }));

      // Validate field in real-time if it's been touched
      validateField(name, value === "" ? "" : parseFloat(value));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);

      // Convert empty strings to null for validation
      const dataToValidate = {
        customCommission:
          globalSettings.customCommission === ""
            ? null
            : parseFloat(globalSettings.customCommission),
        tollTaxPerKm:
          globalSettings.tollTaxPerKm === ""
            ? null
            : parseFloat(globalSettings.tollTaxPerKm),
      };

      // Validate entire form
      const validationErrors = await validateFormAsync(
        globalSettingsSchema,
        dataToValidate
      );

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setTouched({
          customCommission: true,
          tollTaxPerKm: true,
        });

        toast({
          title: "Validation Error",
          description: "Please fix all errors before saving.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem("prima_session");
      await axios.post(
        `${SERVER_URL}/api/global-fields`,
        {
          globalCommission:
            globalSettings.customCommission === ""
              ? null
              : parseFloat(globalSettings.customCommission),
          globalTollTax:
            globalSettings.tollTaxPerKm === ""
              ? null
              : parseFloat(globalSettings.tollTaxPerKm),
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );

      // Clear errors and touched
      setErrors({});
      setTouched({});

      toast({
        title: "Success",
        description: "Global settings have been saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save settings",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSavingSettings(false);
    }
  };

  if (isLoading) {
    return (
      <Box px={4} py={6}>
        <BreadCrumbMaker
          items={[
            { link: "/management", title: "Operations Setup" },
            { link: "/management/global-fields", title: "Global Fields" },
          ]}
        />
        <VStack align="stretch" spacing={6} mt={6}>
          <Box bg="white" p={8} rounded="lg" border="1px solid #E8EDF7" boxShadow="sm">
            <Text>Loading...</Text>
          </Box>
        </VStack>
      </Box>
    );
  }

  return (
    <Box px={4} py={6}>
      <BreadCrumbMaker
        items={[
          { link: "/management", title: "Operations Setup" },
          { link: "/management/global-fields", title: "Global Fields" },
        ]}
      />

      <VStack align="stretch" spacing={6} mt={6}>
        <Box
          bg="white"
          p={8}
          rounded="lg"
          border="1px solid #E8EDF7"
          boxShadow="sm"
        >
          <Heading fontSize="lg" mb={6} color="blue.600">
            Global Custom Booking Settings
          </Heading>

          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} mb={6}>
            <GridItem>
              <FormControl isInvalid={!!errors.customCommission && touched.customCommission}>
                <FormLabel fontSize="sm" fontWeight="500" mb={2}>
                  Custom Commission (%)
                  <Text as="span" color="red.500" fontSize="sm">
                    *
                  </Text>
                </FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color="gray.500">
                    %
                  </InputLeftElement>
                  <Input
                    type="text"
                    inputMode="decimal"
                    name="customCommission"
                    value={globalSettings.customCommission}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="0.00"
                    paddingLeft="30px"
                    h="40px"
                    min="0"
                    max="100"
                    step="0.01"
                    borderColor={
                      errors.customCommission && touched.customCommission
                        ? "red.500"
                        : "inherit"
                    }
                  />
                </InputGroup>
                {errors.customCommission && touched.customCommission && (
                  <FormErrorMessage fontSize="sm" mt={1}>
                    {errors.customCommission}
                  </FormErrorMessage>
                )}
                {!errors.customCommission && touched.customCommission && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Range: 0% - 100%, up to 2 decimal places
                  </Text>
                )}
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={!!errors.tollTaxPerKm && touched.tollTaxPerKm}>
                <FormLabel fontSize="sm" fontWeight="500" mb={2}>
                  Toll Tax Per KM (₹)
                  <Text as="span" color="red.500" fontSize="sm">
                    *
                  </Text>
                </FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color="gray.500">
                    ₹
                  </InputLeftElement>
                  <Input
                    type="text"
                    inputMode="decimal"
                    name="tollTaxPerKm"
                    value={globalSettings.tollTaxPerKm}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="0.00"
                    paddingLeft="30px"
                    h="40px"
                    min="0"
                    max="10000"
                    step="0.01"
                    borderColor={
                      errors.tollTaxPerKm && touched.tollTaxPerKm
                        ? "red.500"
                        : "inherit"
                    }
                  />
                </InputGroup>
                {errors.tollTaxPerKm && touched.tollTaxPerKm && (
                  <FormErrorMessage fontSize="sm" mt={1}>
                    {errors.tollTaxPerKm}
                  </FormErrorMessage>
                )}
                {!errors.tollTaxPerKm && touched.tollTaxPerKm && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Range: ₹0 - ₹10,000, up to 2 decimal places
                  </Text>
                )}
              </FormControl>
            </GridItem>
          </Grid>

          <Button
            colorScheme="blue"
            size="md"
            onClick={handleSaveSettings}
            isLoading={savingSettings}
            isDisabled={
              Object.keys(errors).length > 0 ||
              (!globalSettings.customCommission && !globalSettings.tollTaxPerKm)
            }
          >
            Save Global Settings
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

export default GlobalFields;